import { db } from './firebase';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';

const RAILWAY_WS_URL = process.env.REACT_APP_RECORDING_SERVER_URL;

class TranscriptService {
  constructor() {
    this.socket = null;
    this.mediaRecorder = null;
    this.audioStream = null;

    this.isListening = false;
    this.roomId = null;
    this.speakerRole = 'candidate';

    this.micPermissionGranted = false;
    this.micPermissionChecked = false;

    this.listeners = [];

    // Buffer last interim transcript so we can save it when is_final arrives empty
    this._lastInterimTranscript = '';
  }

  // ── SUBSCRIPTIONS ─────────────────────────

  onTranscript(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners(data) {
    this.listeners.forEach(listener => {
      try { listener(data); } catch (e) { console.error('Transcript listener error:', e); }
    });
  }

  // ── PERMISSION ────────────────────────────

  async requestMicrophonePermission() {
    if (this.micPermissionChecked) return this.micPermissionGranted;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      this.micPermissionGranted = true;
      console.log('✅ Microphone permission granted');
    } catch (error) {
      console.error('❌ Microphone permission denied:', error);
      this.micPermissionGranted = false;
    }

    this.micPermissionChecked = true;
    return this.micPermissionGranted;
  }

  // ── PRE-WARM ──────────────────────────────

  async prewarm() {
    await this.requestMicrophonePermission();
    console.log('🔥 TranscriptService pre-warmed');
  }

  // ── RAILWAY PROXY WEBSOCKET ───────────────

  _openSocket() {
    return new Promise((resolve, reject) => {
      if (!RAILWAY_WS_URL) {
        return reject(new Error('REACT_APP_RECORDING_SERVER_URL is not set'));
      }

      const url = `${RAILWAY_WS_URL}/ws/${this.roomId}/stt`;
      console.log('🔌 Connecting to STT proxy:', url);

      const socket = new WebSocket(url);

      const timeout = setTimeout(() => {
        reject(new Error('STT proxy connection timed out'));
        socket.close();
      }, 8000);

      socket.onopen = () => {
        console.log('✅ STT proxy WebSocket opened, waiting for READY...');
      };

      socket.onmessage = async (event) => {
        const msg = event.data;

        // Handle Blob (binary frame) — convert to text first
        const text = msg instanceof Blob ? await msg.text() : msg;

        if (text === 'READY') {
          clearTimeout(timeout);
          this.socket = socket;
          console.log('✅ STT proxy ready');
          resolve(socket);
          return;
        }

        if (text === 'PONG') return;

        try {
          const data = JSON.parse(text);
          this._handleDeepgramMessage(data);
        } catch (e) {
          console.error('❌ Failed to parse STT message:', e);
        }
      };

      socket.onerror = (err) => {
        clearTimeout(timeout);
        console.error('❌ STT proxy WebSocket error:', err);
        reject(err);
      };

      socket.onclose = (event) => {
        console.log('🔌 STT proxy closed:', event.code, event.reason);
        if (this.isListening) {
          console.log('🔄 Restarting STT connection...');
          setTimeout(() => {
            if (this.isListening) this._restartListening();
          }, 1000);
        }
      };
    });
  }

  _handleDeepgramMessage(data) {
    if (data.type !== 'Results') return;

    const transcript = data?.channel?.alternatives?.[0]?.transcript?.trim();
    const isFinal = data.is_final;

    console.log('📝 Transcript:', transcript, '| final:', isFinal, '| speech_final:', data.speech_final);

    if (transcript) {
      // Notify listeners with latest text (interim or final)
      this.notifyListeners({
        text: transcript,
        speakerRole: this.speakerRole,
        timestamp: new Date(),
        isFinal,
      });

      if (!isFinal) {
        // Buffer interim transcript — Deepgram often sends text here but not in is_final
        this._lastInterimTranscript = transcript;
      }
    }

    if (isFinal) {
      // Use buffered interim if final came in empty (common Deepgram behavior)
      const textToSave = transcript || this._lastInterimTranscript;
      if (textToSave) {
        console.log('💾 Saving to Firebase:', textToSave);
        this.saveTranscriptToFirebase(textToSave);
      }
      this._lastInterimTranscript = '';
    }
  }

  // existingStream — pass LiveKit's mic MediaStream to avoid opening mic twice.
  // If not provided, falls back to getUserMedia (e.g. for testing).
  async _startMediaRecorder(existingStream = null) {
    const stream = existingStream || await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 16000,
      },
    });

    // Only store stream for cleanup if we created it ourselves.
    // If it came from LiveKit, LiveKit owns it — don't stop its tracks.
    this.audioStream = existingStream ? null : stream;

    const mimeType = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ].find(type => MediaRecorder.isTypeSupported(type)) || '';

    const recorder = new MediaRecorder(stream, {
      ...(mimeType ? { mimeType } : {}),
      audioBitsPerSecond: 16000,
    });

    recorder.ondataavailable = (event) => {
      if (
        event.data &&
        event.data.size > 0 &&
        this.socket &&
        this.socket.readyState === WebSocket.OPEN
      ) {
        this.socket.send(event.data);
      }
    };

    recorder.onerror = (event) => {
      console.error('❌ MediaRecorder error:', event.error);
    };

    recorder.start(250);
    this.mediaRecorder = recorder;
    console.log('🎙️ MediaRecorder started, mimeType:', recorder.mimeType);
  }

  async _restartListening() {
    this._cleanupMedia();
    try {
      await this._openSocket();
      await this._startMediaRecorder(); // falls back to getUserMedia on restart
    } catch (err) {
      console.error('❌ Failed to restart listening:', err);
    }
  }

  _cleanupMedia() {
    if (this.mediaRecorder) {
      try {
        if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
      } catch (e) { /* ignore */ }
      this.mediaRecorder = null;
    }

    // Only stop tracks if we own the stream (not LiveKit's stream)
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }

    if (this.socket) {
      try {
        if (this.socket.readyState === WebSocket.OPEN) this.socket.send('END');
        this.socket.close();
      } catch (e) { /* ignore */ }
      this.socket = null;
    }
  }

  // ── START / STOP ──────────────────────────

  // Pass existingStream (from LiveKit mic track) to avoid opening mic twice.
  async startListening(existingStream = null) {
    const hasPermission = await this.requestMicrophonePermission();
    if (!hasPermission) {
      console.error('❌ No microphone permission');
      return false;
    }

    if (this.isListening) {
      console.log('🎤 Already listening');
      return true;
    }

    if (!this.roomId) {
      console.error('❌ roomId not set — call setRoom() first');
      return false;
    }

    try {
      await this._openSocket();
      await this._startMediaRecorder(existingStream);
      this.isListening = true;
      console.log('🎤 Transcription started via Railway proxy');
      return true;
    } catch (error) {
      console.error('❌ Failed to start transcription:', error);
      this._cleanupMedia();
      return false;
    }
  }

  stopListening() {
    if (!this.isListening) return false;
    this.isListening = false;
    this._cleanupMedia();
    console.log('🛑 Transcription stopped');
    return true;
  }

  // ── SETTERS ───────────────────────────────

  setRoom(roomId) {
    this.roomId = roomId;
    this.prewarm();
    return this;
  }

  setSpeakerRole(role) {
    const validRoles = ['interviewer', 'candidate'];
    if (validRoles.includes(role)) {
      this.speakerRole = role;
      console.log('👤 Speaker role:', role);
      return this;
    }
    console.error('❌ Invalid role:', role);
    return this;
  }

  // ── FIREBASE ──────────────────────────────

  async saveTranscriptToFirebase(text) {
    if (!this.roomId || !text?.trim()) return false;

    try {
      await setDoc(
        doc(db, 'rooms', this.roomId, 'transcripts', Date.now().toString()),
        { sender: this.speakerRole, text: text.trim(), timestamp: serverTimestamp() }
      );
      console.log('✅ Transcript saved to Firebase:', text.trim());
      return true;
    } catch (error) {
      console.error('❌ Error saving transcript:', error);
      return false;
    }
  }

  // ── CLEANUP ───────────────────────────────

  cleanup() {
    console.log('🧹 Cleaning up TranscriptService');
    this.isListening = false;
    this._lastInterimTranscript = '';
    this._cleanupMedia();
    this.listeners = [];
    this.micPermissionChecked = false;
    this.micPermissionGranted = false;
  }
}

export default TranscriptService;